/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/no-array-index-key */
/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  TooltipProps,
} from 'recharts';
import { Box, Size, Typography, useThemeValue } from '@altermaind/web/ui';
import { convertSizeToNumber } from '@/_shared/helpers';
import { NavigationForChart } from '../NavigationForChart';

type Props = {
  data: {
    name: string;
    value: number;
    value2?: number;
    value3?: number;
    label: string;
    total?: number;
  }[];
  domain?: [number, number];
  domainHasInPercentage?: boolean;
  hasNoXAxis?: boolean;
  customTooltipBox?: (props: any) => JSX.Element | null;
  hasMultipleValues?: boolean;
  defaultItemsSize?: number;
  startYAxisFrom?: Size;
};

export const BarChartComponent: FC<Props> = ({
  data,
  domain = [0, 100],
  domainHasInPercentage = true,
  hasNoXAxis = false,
  customTooltipBox,
  hasMultipleValues = false,
  defaultItemsSize = 5,
  startYAxisFrom,
}) => {
  const DEFAULT_ITEMS_SIZE = defaultItemsSize;
  const [slide, setSlide] = useState(0);
  const primaryColor = useThemeValue('colors', 'primary');
  const YAxiesSize = convertSizeToNumber(useThemeValue('sizes', startYAxisFrom || 'xs') || 10);
  const YAxisWidth = startYAxisFrom ? YAxiesSize : undefined;
  const radius = 10;

  const CustomBarShape = (props: any) => {
    const { x, y, width, height, fill } = props;
    const r = Math.min(radius, width / 2, height);

    return (
      <path
        d={`
          M${x},${y + height}
          L${x},${y + r}
          Q${x},${y} ${x + r},${y}
          L${x + width - r},${y}
          Q${x + width},${y} ${x + width},${y + r}
          L${x + width},${y + height}
          Z
        `}
        fill={fill}
      />
    );
  };

  const CustomTooltip = ({ active, payload }: TooltipProps<number, string>) => {
    if (active && payload && payload.length) {
      return (
        <Typography
          style={{
            backgroundColor: '#fff',
            color: '#000',
            borderRadius: '8px',
            padding: '6px 10px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
            fontSize: '14px',
            border: 'none',
          }}
        >
          {payload[0].payload.label}
        </Typography>
      );
    }
    return null;
  };

  const Chart = ({ start = 0, length = data.length }) => (
    <ResponsiveContainer width='100%' height={400}>
      <BarChart data={data.slice(start, length)}>
        <CartesianGrid strokeDasharray='3 3' stroke='#444' vertical={false} />
        {!hasNoXAxis && (
          <XAxis
            interval={0}
            minTickGap={0}
            tick={({ x, y, payload }) => {
              const lines = String(payload.value).split('\n');
              return (
                // eslint-disable-next-line @altermaind/no-magic-numbers
                <text x={x} y={y + 10} textAnchor='middle' fill='#666'>
                  {lines.map((line, index) => (
                    <tspan key={line} x={x} dy={index === 0 ? 0 : 14}>
                      {line}
                    </tspan>
                  ))}
                </text>
              );
            }}
            dataKey='name'
            tickLine={false}
            axisLine={false}
          />
        )}
        <YAxis
          tickLine={false}
          axisLine={false}
          tickFormatter={(value: number) => (domainHasInPercentage ? `${value}%` : `${value}`)}
          domain={domain}
          width={YAxisWidth}
        />
        <Tooltip
          cursor={{ fill: 'rgba(0,0,0,0.2)' }}
          // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
          content={customTooltipBox || CustomTooltip}
          contentStyle={{
            backgroundColor: '#fff',
            color: '#000',
            borderRadius: '8px',
            padding: '6px 10px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
            fontSize: '14px',
            border: 'none',
          }}
        />
        <Bar
          dataKey='value'
          barSize={30}
          radius={[radius, radius, 0, 0]}
          shape={(props: any) => <CustomBarShape {...props} fill={primaryColor} />}
        />
        {hasMultipleValues && (
          <>
            {data.map(item => item.value2).some(val => val !== undefined) && (
              <Bar
                dataKey='value2'
                barSize={30}
                radius={[radius, radius, 0, 0]}
                shape={(props: any) => <CustomBarShape {...props} fill='rgba(176, 128, 255, 1)' />}
              />
            )}
            {data.map(item => item.value3).some(val => val !== undefined) && (
              <Bar
                dataKey='value3'
                barSize={30}
                radius={[radius, radius, 0, 0]}
                shape={(props: any) => <CustomBarShape {...props} fill='white' />}
              />
            )}
          </>
        )}
      </BarChart>
    </ResponsiveContainer>
  );

  if (data.length > DEFAULT_ITEMS_SIZE) {
    return (
      <Box width='100%'>
        <Chart start={slide * DEFAULT_ITEMS_SIZE} length={(slide + 1) * DEFAULT_ITEMS_SIZE} />
        <NavigationForChart
          onClick={setSlide}
          data={data}
          DEFAULT_ITEMS_SIZE={DEFAULT_ITEMS_SIZE}
          slide={slide}
        />
      </Box>
    );
  }

  return <Chart />;
};
